// Human-readable descriptions of widget manifest permissions — what the installer
// (and the reviewer) sees before letting third-party UI code run in their workspace.
// Mirrors the server's WidgetManifestRules: sandbox tokens, bridge calls, api: scopes
// and network: origins. Anything else is rejected at upload, so an unknown value here
// can only come from a stale/mismatched client.

import type { WidgetManifest } from '../../services/myToolsApi';
import { API_PERMISSION_PREFIX, WIDGET_API_SCOPES } from './widgetDataAccess';

export type PermissionGroup = 'data' | 'network' | 'browser' | 'sandbox';
export type PermissionRisk = 'low' | 'medium' | 'high';

export interface PermissionDescription {
  /** Raw manifest value, e.g. "api:policies.read". */
  key: string;
  group: PermissionGroup;
  icon: string;
  label: string;
  description: string;
  risk: PermissionRisk;
  /** True for scopes that can change agency data. */
  write?: boolean;
}

const NETWORK_PREFIX = 'network:';
const GROUP_ORDER: PermissionGroup[] = ['data', 'network', 'browser', 'sandbox'];
const RISK_ORDER: PermissionRisk[] = ['high', 'medium', 'low'];

const STATIC: Record<string, Omit<PermissionDescription, 'key'>> = {
  'forms': { group: 'sandbox', icon: '📝', label: 'Submit forms', description: 'Can submit HTML forms inside its own frame. Forms can only post to declared network origins.', risk: 'low' },
  'popups': { group: 'sandbox', icon: '🪟', label: 'Open pop-up windows', description: 'Can open new browser windows. They stay sandboxed and cannot reach Momentum AMP.', risk: 'medium' },
  'modals': { group: 'sandbox', icon: '💬', label: 'Show browser dialogs', description: 'Can show alert, confirm and prompt dialogs while you use it.', risk: 'low' },
  'open-links': { group: 'browser', icon: '🔗', label: 'Open links in a new tab', description: 'Can ask Momentum AMP to open https:// links in a new browser tab.', risk: 'low' },
  'clipboard-write': { group: 'browser', icon: '📋', label: 'Copy to your clipboard', description: 'Can place text on your clipboard when you interact with it.', risk: 'low' },
  'ui:navigate': { group: 'browser', icon: '🧭', label: 'Navigate inside Momentum AMP', description: 'Can take you to another page in Momentum AMP (e.g. an insured or policy). Only in-app routes — never external sites.', risk: 'low' },
  'ui:notify': { group: 'browser', icon: '🔔', label: 'Show notifications', description: 'Can show short toast messages in Momentum AMP, at most one every few seconds.', risk: 'low' },
  'chat:prefill': { group: 'browser', icon: '💬', label: 'Draft a chat message', description: 'Can put text into the AI chat box for you. Nothing is sent until you press send.', risk: 'low' },
  'realtime:kanban': { group: 'data', icon: '📡', label: 'Live task updates', description: 'Gets notified when your kanban tasks change, so it can refresh itself without you reloading. Momentum AMP forwards a bare "something changed" signal — the widget still has to ask for the details it\'s allowed to see.', risk: 'medium' },
};

export function describeWidgetPermission(raw: string): PermissionDescription {
  const key = String(raw ?? '').trim();
  const lower = key.toLowerCase();
  const fixed = STATIC[lower];
  if (fixed) return { key, ...fixed };

  if (lower.startsWith(API_PERMISSION_PREFIX)) {
    const scope = lower.slice(API_PERMISSION_PREFIX.length);
    const definition = WIDGET_API_SCOPES[scope];
    const write = scope.endsWith('.write');
    return {
      key,
      group: 'data',
      icon: write ? '✏️' : '📂',
      label: definition?.label ?? `Data scope "${scope}"`,
      description: definition
        ? `${definition.description} Requests go through Momentum AMP with your own access rights — the widget never receives your login token.`
        : 'Unknown data scope. Momentum AMP refuses these requests at runtime.',
      risk: write ? 'high' : 'medium',
      write,
    };
  }

  if (lower.startsWith(NETWORK_PREFIX)) {
    const origin = key.slice(NETWORK_PREFIX.length).replace(/\/+$/, '');
    return {
      key,
      group: 'network',
      icon: '🌐',
      label: `Connect to ${origin || 'an external site'}`,
      description: 'Can exchange data with this external website directly from your browser. Anything the widget shows or you type into it could be sent there.',
      risk: 'high',
    };
  }

  return {
    key,
    group: 'sandbox',
    icon: '❓',
    label: key || 'Unknown permission',
    description: 'Unrecognised permission. The server rejects unknown values at upload, so this bundle may be out of date.',
    risk: 'medium',
  };
}

/** All permissions of a manifest, de-duplicated and ordered: data → network → browser → sandbox, riskiest first. */
export function describeWidgetPermissions(manifest: WidgetManifest | null | undefined): PermissionDescription[] {
  const seen = new Set<string>();
  const items: PermissionDescription[] = [];
  for (const raw of manifest?.permissions ?? []) {
    const described = describeWidgetPermission(raw);
    const dedupeKey = described.key.toLowerCase();
    if (!dedupeKey || seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    items.push(described);
  }
  return items.sort((a, b) =>
    GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group)
    || RISK_ORDER.indexOf(a.risk) - RISK_ORDER.indexOf(b.risk)
    || a.label.localeCompare(b.label));
}

/** Installers must explicitly acknowledge widgets that reach agency data or external sites. */
export function widgetNeedsAcknowledgement(manifest: WidgetManifest | null | undefined): boolean {
  return describeWidgetPermissions(manifest).some(p => p.group === 'data' || p.group === 'network');
}

/** One-line acknowledgement text for the install prompt. */
export function widgetAcknowledgementText(manifest: WidgetManifest | null | undefined, forAgency: boolean): string {
  const items = describeWidgetPermissions(manifest);
  const reads = items.filter(p => p.group === 'data' && !p.write).length;
  const writes = items.filter(p => p.group === 'data' && p.write).length;
  const sites = items.filter(p => p.group === 'network').length;
  const parts: string[] = [];
  if (reads) parts.push('read agency data');
  if (writes) parts.push('create records');
  if (sites) parts.push(`talk to ${sites === 1 ? 'an external site' : `${sites} external sites`}`);
  const what = parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  const who = forAgency ? 'on behalf of everyone in my agency' : 'on my behalf';
  return `I understand this widget can ${what} ${who}, using the access I already have, and that it was built by its author — not by Momentum AMP.`;
}

/** Compact summary for cards/badges, e.g. "Reads policies · 1 external site". */
export function summarizeWidgetPermissions(manifest: WidgetManifest | null | undefined): string {
  const items = describeWidgetPermissions(manifest);
  if (items.length === 0) return 'Isolated — no data or network access';
  const parts: string[] = [];
  const data = items.filter(p => p.group === 'data');
  if (data.length) parts.push(data.length === 1 ? data[0].label : `${data.length} data scopes`);
  const sites = items.filter(p => p.group === 'network').length;
  if (sites) parts.push(`${sites} external site${sites === 1 ? '' : 's'}`);
  const rest = items.length - data.length - sites;
  if (rest) parts.push(`${rest} browser capabilit${rest === 1 ? 'y' : 'ies'}`);
  return parts.join(' · ');
}
